import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Tag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BUILTINS = [
  { key: "REF_LINK", desc: "Ambassador's personal referral link" },
  { key: "CODE", desc: "Ambassador's referral code (also [REF])" },
  { key: "NAME", desc: "Ambassador's name" },
];

export default function PlaceholdersManager({ onChange }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/placeholders").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const addRow = () => setItems([...items, { key: "", value: "" }]);
  const update = (i, field, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)));
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/placeholders", { items });
      setItems(r.data.items || []);
      onChange?.(r.data.items || []);
      toast.success("Placeholders saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-5" data-testid="placeholders-manager">
      <div className="flex items-center gap-2"><Tag className="h-4 w-4 text-[#A855F7]" /><h3 className="font-display text-lg font-bold">Caption placeholders</h3></div>
      <p className="mt-1 text-xs text-zinc-500">Use these inside any caption template — they auto-replace when an ambassador views it. Both <code className="text-zinc-300">[KEY]</code> and <code className="text-zinc-300">{"{KEY}"}</code> work.</p>

      <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
        <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Built-in (per ambassador)</p>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {BUILTINS.map((b) => (
            <span key={b.key} className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-[11px] text-zinc-300" title={b.desc}>[{b.key}]</span>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Custom (fixed value — e.g. INNOVATE_END, BRAND_END, PRIZE_POOL)</p>
        <div className="mt-2 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`placeholder-row-${i}`}>
              <input
                value={it.key}
                onChange={(e) => update(i, "key", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))}
                placeholder="INNOVATE_END"
                className="w-40 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 font-mono text-xs uppercase"
                data-testid={`placeholder-key-${i}`}
              />
              <span className="text-zinc-600">→</span>
              <input
                value={it.value}
                onChange={(e) => update(i, "value", e.target.value)}
                placeholder="4 Jul 2026"
                className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-xs"
                data-testid={`placeholder-value-${i}`}
              />
              <button onClick={() => remove(i)} className="rounded p-1.5 text-rose-400 hover:bg-rose-500/10" data-testid={`placeholder-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={addRow} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-900" data-testid="placeholder-add"><Plus className="h-3.5 w-3.5" /> Add placeholder</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-[#A855F7] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#9333EA] disabled:opacity-50" data-testid="placeholder-save"><Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save placeholders"}</button>
        </div>
      </div>
    </div>
  );
}
